/**
 * Interval-Based Position Utilities
 *
 * Determines which positions should display symbols/labels based on
 * interval rules and explicit overrides.
 */
import { parseDuration } from './duration.js';
import { InvalidPointShapeError } from './errors.js';
import { PointShapeEnum } from '@debrief/schemas';
/**
 * Cached at module load so the resolver's per-call guard (FR-015) does a
 * constant-time Set lookup instead of building an array every invocation.
 */
const VALID_POINT_SHAPES = new Set(Object.values(PointShapeEnum));
/**
 * Find position indices that match a time interval.
 *
 * Given an array of timestamps and an interval duration, returns the indices
 * of positions that fall at or near interval boundaries from the start time.
 *
 * Uses "nearest position" logic when intervals don't align exactly with
 * position timestamps.
 *
 * @param timestamps Array of epoch timestamps (ms) for each position
 * @param intervalMs Interval duration in milliseconds
 * @returns Set of position indices that match the interval
 */
export function findIntervalPositions(timestamps, intervalMs) {
    const result = new Set();
    if (timestamps.length === 0 || intervalMs <= 0) {
        return result;
    }
    // Always include the first and last positions so short tracks never lose
    // their endpoints to interval-rounding gaps (the components-side resolver
    // historically guaranteed this — FR-008 requires behaviour preservation).
    result.add(0);
    if (timestamps.length > 1) {
        result.add(timestamps.length - 1);
    }
    const startTime = timestamps[0];
    const endTime = timestamps[timestamps.length - 1];
    // Calculate each interval mark from start time
    let intervalTime = startTime;
    while (intervalTime <= endTime) {
        // Find nearest position to this interval time
        const nearestIdx = findNearestPositionIndex(timestamps, intervalTime);
        if (nearestIdx >= 0) {
            result.add(nearestIdx);
        }
        intervalTime += intervalMs;
    }
    return result;
}
/**
 * Find the index of the position nearest to the target time.
 *
 * @param timestamps Array of epoch timestamps (ms)
 * @param targetTime Target time to find nearest position to
 * @returns Index of nearest position, or -1 if array is empty
 */
export function findNearestPositionIndex(timestamps, targetTime) {
    if (timestamps.length === 0) {
        return -1;
    }
    if (timestamps.length === 1) {
        return 0;
    }
    // Binary search for efficiency with large tracks
    let low = 0;
    let high = timestamps.length - 1;
    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (timestamps[mid] < targetTime) {
            low = mid + 1;
        }
        else {
            high = mid;
        }
    }
    // Compare with adjacent position to find nearest
    if (low === 0) {
        return 0;
    }
    const diffLow = Math.abs(timestamps[low] - targetTime);
    const diffPrev = Math.abs(timestamps[low - 1] - targetTime);
    return diffPrev <= diffLow ? low - 1 : low;
}
/**
 * Resolve the style for a single position using the cascade:
 * default_position_style → interval rules → position_style_overrides
 *
 * @param index Position index
 * @param defaultStyle Default position style for the track
 * @param symbolIntervalPositions Set of positions matching symbol interval
 * @param labelIntervalPositions Set of positions matching label interval
 * @param override Position-specific override (may be null)
 * @param positionTime ISO timestamp for this position (used as default label)
 * @returns Resolved style for rendering
 */
export function resolvePositionStyle(index, defaultStyle, symbolIntervalPositions, labelIntervalPositions, override, positionTime) {
    // 1. Start with defaults
    let showSymbol = defaultStyle.show_symbol;
    let symbol = defaultStyle.symbol;
    let showLabel = defaultStyle.show_label;
    let labelText = null;
    // 2. Apply interval rules (intervals enable, they don't disable)
    if (symbolIntervalPositions.has(index)) {
        showSymbol = true;
    }
    if (labelIntervalPositions.has(index)) {
        showLabel = true;
    }
    // 3. Apply explicit override (highest priority).
    //    Treat `null` as "no override — use cascaded default" per FR-013 so
    //    that e.g. `{ show_symbol: null }` preserves the default instead of
    //    toggling the flag off.
    if (override) {
        if (override.show_symbol !== undefined && override.show_symbol !== null) {
            showSymbol = override.show_symbol;
        }
        if (override.symbol !== undefined && override.symbol !== null) {
            if (!VALID_POINT_SHAPES.has(override.symbol)) {
                throw new InvalidPointShapeError(override.symbol, [
                    ...VALID_POINT_SHAPES,
                ]);
            }
            symbol = override.symbol;
        }
        if (override.show_label !== undefined && override.show_label !== null) {
            showLabel = override.show_label;
        }
        if (override.label !== undefined && override.label !== null) {
            labelText = override.label;
        }
    }
    // 4. Default label text to formatted timestamp
    if (showLabel && labelText === null) {
        labelText = formatTimestampForLabel(positionTime);
    }
    return {
        showSymbol,
        // schema PositionStyle.symbol is currently `string`; the guard above
        // ensures override-sourced values are real PointShapes, and the schema
        // fixtures + LinkML constraints ensure default-style values are too.
        symbol: symbol,
        showLabel,
        labelText,
    };
}
/**
 * Format a timestamp for position label display.
 *
 * @param isoTimestamp ISO 8601 timestamp string
 * @returns Formatted time string (HH:MM:SS)
 */
export function formatTimestampForLabel(isoTimestamp) {
    try {
        const date = new Date(isoTimestamp);
        return date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    }
    catch {
        return isoTimestamp;
    }
}
/**
 * Compute all resolved styles for a track's positions.
 *
 * @param positions Array of position timestamps (ISO strings)
 * @param defaultStyle Default position style
 * @param symbolInterval Symbol interval string (ISO 8601 duration) or null
 * @param labelInterval Label interval string (ISO 8601 duration) or null
 * @param overrides Array of overrides (parallel to positions, may contain nulls)
 * @returns Array of resolved styles (parallel to positions)
 */
export function computeAllPositionStyles(positions, defaultStyle, symbolInterval, labelInterval, overrides) {
    // Parse timestamps to epoch ms for interval calculations
    const timestamps = positions.map((p) => new Date(p.time).getTime());
    // Compute interval positions
    const symbolIntervalMs = parseDuration(symbolInterval);
    const labelIntervalMs = parseDuration(labelInterval);
    const symbolIntervalPositions = symbolIntervalMs
        ? findIntervalPositions(timestamps, symbolIntervalMs)
        : new Set();
    const labelIntervalPositions = labelIntervalMs
        ? findIntervalPositions(timestamps, labelIntervalMs)
        : new Set();
    // session-state persists sparse overrides as an index-keyed object for a
    // compact on-disk shape; schema-typed data uses the array form. Accept
    // either and normalise per position.
    const isArrayOverrides = Array.isArray(overrides);
    // Resolve style for each position
    return positions.map((pos, i) => {
        const override = isArrayOverrides
            ? (overrides[i] ?? null)
            : (overrides?.[String(i)] ?? null);
        return resolvePositionStyle(i, defaultStyle, symbolIntervalPositions, labelIntervalPositions, override, pos.time);
    });
}
//# sourceMappingURL=interval.js.map